import { ButtonNew, LuneThemeNew, LuneThemeProvider, Modal, Text } from '@lune-fe/lune-ui-lib'
import Box from '@mui/material/Box'
import React, { FC, useEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'

import { ParameterName } from '../utils/sharedLogic'
import { parameterValueType } from '../utils/types'
import { countInvalidDataTypeValues, findInvalidColumnIndexes } from '../utils/utils'
const { palette } = LuneThemeNew

interface ColumnPickerProps {
    propertyName: string
    parameterName: ParameterName
    headers: string[]
    headerStartIndex: number
    exampleRow: string[]
    columnIndex: number | undefined
    fetchColumnValues: (index: number) => string[]
}

interface ColumnPickerModalProps extends ColumnPickerProps {
    modalOpen: boolean
    onModalClose: (selection?: number) => void
}

const RadioButton: FC<{ selected: boolean; error: boolean }> = ({ selected, error }) => {
    return (
        <Box
            sx={{
                width: '24px',
                height: '24px',
                border: '2px solid ' + (error ? palette.TextError : palette.TextPrimary),
                borderRadius: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Box
                sx={{
                    borderRadius: '100%',
                    width: '10px',
                    height: '10px',
                    transform: selected ? 'scale(1)' : 'scale(0)',
                    transition: 'transform .1s ease-in-out',
                    background: error ? palette.TextError : palette.TextPrimary,
                }}
            />
        </Box>
    )
}

const Columns: FC<{
    headers: string[]
    headerStartIndex: number
    exampleRow: string[]
    selectedIndex: number | undefined
    onSelect: (index: number) => void
    invalidColumnIndexes: number[]
}> = ({ headers, headerStartIndex, exampleRow, onSelect, invalidColumnIndexes, selectedIndex }) => {
    const [localSelectedIndex, setLocalSelectedIndex] = useState<number | undefined>(selectedIndex)

    const itemRefs = React.useRef<(HTMLButtonElement | null)[]>([])

    useEffect(() => {
        if (selectedIndex !== undefined) {
            itemRefs.current[selectedIndex]?.scrollIntoView({ inline: 'center', block: 'nearest' })
        }
    }, [selectedIndex])

    const style = {
        width: '80px',
        height: '114px',
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: localSelectedIndex || localSelectedIndex === 0 ? palette.Grey50 : palette.White,
        transition: 'background .1s ease-in-out',
        span: {
            maxWidth: '100%',
            whiteSpace: 'break-spaces',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            wordBreak: 'break-word',
            WebkitLineClamp: 5,
            WebkitBoxOrient: 'vertical',
            fontSize: '12px',
        },
    }

    return (
        <Box
            sx={{
                width: 'calc(100% + 96px)',
                overflow: 'auto',
                ml: -6,
                mr: -6,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                }}
            >
                {headers.map((header, index) => {
                    // Don't allow the user to pick headers before the official header start
                    if (index < headerStartIndex) {
                        return <></>
                    }
                    return (
                        <Box
                            component={'button'}
                            key={index}
                            ref={(el) => {
                                itemRefs.current[index] = el as HTMLButtonElement
                            }}
                            sx={{
                                '*': {
                                    boxSizing: 'border-box',
                                },
                                cursor: 'pointer',
                                border: 'none',
                                background: 'none',
                                padding: 0,
                            }}
                            onClick={() => {
                                onSelect(index)
                                setLocalSelectedIndex(index)
                            }}
                        >
                            <Box
                                sx={{
                                    width: '80px',
                                    height: '72px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <RadioButton
                                    selected={index === localSelectedIndex}
                                    error={invalidColumnIndexes.includes(index)}
                                />
                            </Box>
                            <Box
                                sx={{
                                    border: `1px solid ${palette.Grey300}`,
                                    mr: '-1px',
                                }}
                            >
                                <Box
                                    sx={{
                                        ...style,
                                        borderBottom: `1px solid ${palette.Grey300}`,
                                        ...(index === localSelectedIndex && {
                                            background: 'white',
                                        }),
                                    }}
                                >
                                    <Text
                                        variant={'caption'}
                                        sx={{
                                            fontWeight: 700,
                                            ...(invalidColumnIndexes.includes(index)
                                                ? { color: palette.TextError }
                                                : {}),
                                        }}
                                    >
                                        {header}
                                    </Text>
                                </Box>
                                <Box
                                    sx={{
                                        ...style,
                                        ...(index === localSelectedIndex && {
                                            background: 'white',
                                        }),
                                    }}
                                >
                                    <Text
                                        variant={'caption'}
                                        sx={{
                                            ...(invalidColumnIndexes.includes(index)
                                                ? { color: palette.TextError }
                                                : {}),
                                        }}
                                    >
                                        {exampleRow[index]}
                                    </Text>
                                </Box>
                            </Box>
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
}

const ColumnPickerModal: FC<ColumnPickerModalProps> = ({
    propertyName,
    modalOpen,
    onModalClose,
    headers,
    headerStartIndex,
    exampleRow,
    columnIndex,
    parameterName,
    fetchColumnValues,
}) => {
    const [selectedIndex, setSelectedIndex] = useState<number | undefined>(columnIndex)
    const [invalidRowCount, setInvalidRowCount] = useState<number | undefined>(0)
    const invalidColumnIndexes = findInvalidColumnIndexes(
        headers,
        exampleRow,
        parameterValueType[parameterName],
    )

    return (
        <LuneThemeProvider theme={LuneThemeNew}>
            <Modal
                maxWidth={'md'}
                fullWidth
                open={modalOpen}
                onClose={() => onModalClose()}
                warning={
                    invalidRowCount ? (
                        <Text variant={'body2'}>
                            <span style={{ color: palette.TextError }}>Warning:</span> There are{' '}
                            {invalidRowCount} values with invalid data types in the selected column
                        </Text>
                    ) : undefined
                }
                header={
                    <Text variant={'h5'}>
                        Select relevant column for:{' '}
                        <span style={{ textTransform: 'capitalize' }}>{propertyName}</span>
                    </Text>
                }
                subheader={
                    <Text variant={'body1'}>
                        We’ll use data in these columns to set the load of your shipments
                    </Text>
                }
                actions={
                    <>
                        <ButtonNew style={'outline'} onClick={() => onModalClose()}>
                            Cancel
                        </ButtonNew>
                        <ButtonNew onClick={() => onModalClose(selectedIndex)}>
                            Map values
                        </ButtonNew>
                    </>
                }
            >
                <Columns
                    headers={headers}
                    headerStartIndex={headerStartIndex}
                    selectedIndex={columnIndex}
                    exampleRow={exampleRow}
                    onSelect={(newIndex) => {
                        setInvalidRowCount(
                            countInvalidDataTypeValues(
                                fetchColumnValues(newIndex),
                                parameterValueType[parameterName],
                            ),
                        )
                        setSelectedIndex(newIndex)
                    }}
                    invalidColumnIndexes={invalidColumnIndexes}
                />
            </Modal>
        </LuneThemeProvider>
    )
}

export default ColumnPickerModal

export const showColumnPickerModal = ({
    propertyName,
    headers,
    headerStartIndex,
    exampleRow,
    columnIndex,
    parameterName,
    fetchColumnValues,
}: ColumnPickerProps): Promise<number | undefined> => {
    return new Promise((resolve) => {
        const container = document.createElement('div')
        document.body.appendChild(container)
        const root = ReactDOM.createRoot(container)

        const handleClose = (selection?: number) => {
            resolve(selection)
            setTimeout(() => {
                root.unmount()
                container.remove()
            }, 0)
        }

        root.render(
            <ColumnPickerModal
                propertyName={propertyName}
                headers={headers}
                headerStartIndex={headerStartIndex}
                exampleRow={exampleRow}
                modalOpen={true}
                onModalClose={handleClose}
                columnIndex={columnIndex}
                parameterName={parameterName}
                fetchColumnValues={fetchColumnValues}
            />,
        )
    })
}
